import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { makeStyles } from "@material-ui/core/styles";
import { setPastDueSTOCheck } from "../../../../store/Actions";
import { useDispatch } from "react-redux";
import { Box } from "@mui/material";

const useStyles = makeStyles((_theme) => ({
  chartContainer: {
    position: "relative",
    overflow: "hidden",
  },
  sliderContainer: {
    margin: "20px 0",
    padding: "0 30px",
    position: "relative",
    maxWidth: "600px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  sliderWrapper: {
    position: "relative",
    height: "8px",
    display: "flex",
    alignItems: "center",
  },
  slider: {
    WebkitAppearance: "none",
    width: "100%",
    height: "4px",
    background: "#e0e0e0",
    borderRadius: "2px",
    outline: "none",
    "&::-webkit-slider-thumb": {
      WebkitAppearance: "none",
      width: "20px",
      height: "20px",
      background: "#0067A1",
      borderRadius: "50%",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      "&:hover": {
        transform: "scale(1.1)",
        background: "#004d80",
      },
      "&:active": {
        transform: "scale(1)",
      },
    },
  },
  pageIndicator: {
    position: "absolute",
    right: "0",
    bottom: "-24px",
    color: "#666",
    fontSize: "0.8rem",
    fontWeight: 400,
    fontFamily: "system-ui, sans-serif",
    "& span": {
      color: "#0067A1",
      fontWeight: 500,
    },
  },
  progressTrack: {
    position: "absolute",
    height: "4px",
    background: "#0067A1",
    borderRadius: "2px",
    transition: "width 0.3s ease",
  },
}));

const DPBarMultiple = ({ graphData = [], activeTab }) => {
  const classes = useStyles();
  const [borderRadius, setBorderRadius] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 5;
  const dispatch = useDispatch();

  useEffect(() => {
    setCurrentPage(0);
  }, [activeTab]);

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 1000) {
        setBorderRadius(2);
      } else if (screenWidth < 1500) {
        setBorderRadius(5);
      } else {
        setBorderRadius(7);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Data mapping based on `activeTab`
  let categories = [];
  let seriesData = [];
  let yAxisTitle = "";

  if (activeTab === "Primary Payload") {
    categories = graphData.map((item) => item.Metric_type || "Unknown Metric");
    seriesData = [
      {
        name: "Selected Period",
        data: graphData.map((item) => item.Current_year_payload || 0),
      },
      {
        name: "Same period last year",
        data: graphData.map((item) => item.Last_year_payload || 0),
      },
    ];
    yAxisTitle = "Payload %";
  } else if (activeTab === "STO Moves reason") {
    categories = graphData.map((item) => item.Metric_name || "Unknown Metric");
    seriesData = [
      {
        name: "Capacity Constraint",
        data: graphData.map((item) => item.Capacity_constraint || 0),
      },
      {
        name: "Needed for Minimum at Destination",
        data: graphData.map(
          (item) => item.Needed_for_minimum_at_destination || 0
        ),
      },
      {
        name: "Critical Need at Destination",
        data: graphData.map((item) => item.Critical_need_at_destination || 0),
      },
    ];
    yAxisTitle = "# of #STOs";
  } else if (activeTab === "Workload") {
    categories = graphData.map(
      (item) => item.DeployerUserID || "Unknown Deployer"
    );
    seriesData = [
      {
        name: "# SKUs selected period",
        data: graphData.map((item) => item.SKUs || 0),
      },
      {
        name: "# of SKU-STOs",
        data: graphData.map((item) => item.STOs || 0),
      },
      {
        name: "TLs",
        data: graphData.map((item) => item.TLs || 0),
      },
    ];
    yAxisTitle = "Numbers";
  } else if (activeTab === "Recommended Diverts") {
    categories = graphData.map((item) => item.Network || "Unknown Deployer");
    seriesData = [
      {
        name: "Recommended",
        data: graphData.map((item) => item.Recommended_diverts || 0),
      },
      {
        name: "Accepted",
        data: graphData.map((item) => item.Accepted_diverts || 0),
      },
    ];
    yAxisTitle = "Diverts This Month";
  } else if (activeTab === "STRs Required") {
    categories = graphData.map((item) => item.Metric_name || "Unknown Metric");
    seriesData = [
      {
        name: "Recommended STR",
        data: graphData.map((item) => item.Recommended_STR || 0),
      },
      {
        name: "Recommended TL Today",
        data: graphData.map((item) => item.Recommended_TL_Today || 0),
      },
    ];
    yAxisTitle = "STRs approvals required";
  } else if (activeTab === "Revenue") {
    categories = graphData.map((item) => item?.Network || "Unknown Metric");
    seriesData = [
      {
        name: "Ordered Revenue",
        data: graphData.map((item) => item?.Ordered_Revenue || 0),
      },
      {
        name: "Current Revenue",
        data: graphData.map((item) => item?.Current_Revenue || 0),
      },
    ];
    yAxisTitle = "$ Millions";
  } else if (activeTab === "COF") {
    categories = graphData.map((item) => item?.Name || "Unknown Metric");
    seriesData = [
      {
        name: "Selected Scenario",
        data: graphData.map((item) => item?.COF || 0),
      },
      {
        name: "Base Model Run",
        data: graphData.map((item) => item?.baseline_COF || 0),
      },
    ];
    yAxisTitle = "COF %";
  } else if (activeTab === "Margin") {
    categories = graphData.map((item) => item?.Name || "Unknown Metric");
    seriesData = [
      {
        name: "Selected Scenario",
        data: graphData.map((item) => item?.Margin || 0),
      },
      {
        name: "Base Model Run",
        data: graphData.map((item) => item?.baseline_Margin || 0),
      },
    ];
    yAxisTitle = "Margin %";
  } else if (
    activeTab === "Penalties" ||
    activeTab === "Exceptions-Sales Doc"
  ) {
    categories = graphData.map((item) => item?.Name || "Unknown Metric");
    seriesData = [
      {
        name: "Selected Scenario",
        data: graphData.map((item) => item?.Penalty || 0),
      },
      {
        name: "Base Model Run",
        data: graphData.map((item) => item?.baseline_Penalty || 0),
      },
    ];
    yAxisTitle = "$ Penalties";
  } else if (activeTab === "Reason Code-Quantity Cuts") {
    categories = graphData.map((item) => item?.MaterialID || "Unknown Metric");
    seriesData = [
      {
        name: "Reason Code",
        data: graphData.map((item) => item?.Reason_Code || 0),
      },
      {
        name: "Reason Code Opportunity",
        data: graphData.map((item) => item?.Reason_Code_Opportunity || 0),
      },
    ];
    yAxisTitle = "$ Penalties";
  } else if (activeTab === "Reason Code-Late Loads") {
    categories = graphData.map((item) => item?.Business || "Unknown Metric");
    seriesData = [
      {
        name: "Late Loads with Reason Codes",
        data: graphData.map((item) => item?.Late_Loads_with_Reason_Codes || 0),
      },
      {
        name: "Late Loads without Reason Codes",
        data: graphData.map(
          (item) => item?.Late_Loads_without_Reason_Codes || 0
        ),
      },
    ];
    yAxisTitle = "$ Penalties";
  }
  // else if (activeTab === 'Exceptions-Sales Doc') {
  //   categories = graphData.map((item) => item?.Name || 'Unknown Metric');
  //   seriesData = [
  //     {
  //       name: 'Selected Scenario',
  //       data: graphData.map((item) => item?.Penalty || 0),
  //     },
  //     {
  //       name: 'Base Model Run',
  //       data: graphData.map((item) => item?.baseline_Penalty || 0),
  //     },
  //   ];
  //   yAxisTitle = '$ Penalties';
  // }

  const formatToKOrM = (value) => {
    if (value >= 1e6) {
      return `${(value / 1e6).toFixed(2)}M`;
    }
    if (value >= 1e3) {
      return `${(value / 1e3).toFixed(2)}K`;
    }
    return value.toFixed(2);
  };

  // Pagination logic
  const totalItems = graphData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const slicedCategories = categories.slice(startIndex, endIndex);
  const slicedSeriesData = seriesData.map((s) => ({
    ...s,
    data: s.data.slice(startIndex, endIndex),
  }));
  const wideColumnTabs = ["COF", "Margin", "Penalties"];
  // Chart options
  const options = {
    chart: {
      offsetY: 10,
      height: 350,
      zoom: {
        enabled: true,
        type: "x",
        autoScaleYaxis: true,
      },
      type: "bar",
      stacked: false,
      toolbar: { show: false },
      redrawOnWindowResize: true,
      redrawOnParentResize: true,
      animations: {
        enabled: true,
        easing: "easeInOutQuad",
        speed: 600,
        animateGradually: {
          enabled: true,
          delay: 150,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
      },
      events: {
        dataPointSelection: (_event, _chartContext, config) => {
          const { dataPointIndex } = config;
          // const selectedSeries = slicedSeriesData[seriesIndex];
          const selectedCategory = slicedCategories[dataPointIndex];
          const selectedOption = graphData.find(
            (item) => item.Metric_name === selectedCategory
          );
          let apiData = {
            selectedOption: selectedOption,
            PastDueFlag: true,
            tab: activeTab,
          };
          dispatch(setPastDueSTOCheck(apiData));
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        // columnWidth: '20%',
        columnWidth:
          yAxisTitle === "Margin" || wideColumnTabs.includes(activeTab)
            ? "10%"
            : "20%",
        borderRadius: borderRadius,
        borderRadiusApplication: "end",
      },
    },
    dataLabels: { enabled: false },
    stroke: { width: 1 },
    series: slicedSeriesData,
    colors: ["#EC6FBD", "#A4D65D", "#0067A1"],
    xaxis: {
      tickAmount: slicedCategories.length,
      tickPlacement: "on",
      categories: slicedCategories,
      labels: {
        maxHeight: 100,
        rotate: -45,
        show: true,
        trim: true,
        hideOverlappingLabels: false,
        showDuplicates: true,
        rotateAlways: true,
        cssClass: "force-xaxis-rotate",
        style: {
          fontSize: "12px",
          fontFamily: "Poppins, sans-serif",
          transform: "rotate(-45deg) !important",
          transformOrigin: "top right !important",
          whiteSpace: "pre-line",
        },
      },
    },
    yaxis: {
      title: {
        text: yAxisTitle,
        style: {
          fontSize: "12px",
          fontFamily: "Poppins, sans-serif",
          fontWeight: 500,
          color: "#333",
        },
      },
      labels: {
        formatter: (value) =>
          activeTab === "Revenue" || activeTab === "Penalties"
            ? `${formatToKOrM(value)}`
            : value.toFixed(0),
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val) => {
          if (val == null) {
            return "";
          } // handle undefined/null gracefully

          if (["Revenue", "COF", "Margin", "Penalties"].includes(activeTab)) {
            return formatToKOrM(val);
          } else {
            return val.toFixed(2);
          }
        },
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
      markers: {
        width: 12,
        height: 12,
        radius: 10,
      },
    },
    grid: {
      show: false,
      padding: {
        bottom: 30, // 👈 increase if labels are still clipped
      },
    },
  };

  return (
    <div className="grouped-bar-chart">
      <Box
        sx={{
          minWidth: slicedCategories.length * 80,
          height: 420,
          width: "100%",
          overflow: "visible",
          overflowX: "auto",
        }}
      >
        <ReactApexChart
          options={options}
          series={slicedSeriesData}
          type="bar"
          height={350}
        />
      </Box>

      {totalPages > 1 &&
        (activeTab === "Primary Payload" ||
          activeTab === "Workload" ||
          activeTab === "STO Moves reason" ||
          activeTab === "STRs Required") && (
          <div className={classes.sliderContainer}>
            <div className={classes.sliderWrapper}>
              <div
                className={classes.progressTrack}
                style={{
                  width: `${((currentPage + 1) / totalPages) * 100}%`,
                  opacity: 0.3,
                }}
              />
              <input
                type="range"
                min="0"
                max={totalPages - 1}
                value={currentPage}
                onChange={(e) => setCurrentPage(parseInt(e.target.value, 10))}
                className={classes.slider}
              />
            </div>
            <div className={classes.pageIndicator}>
              <span>{currentPage + 1}</span> / {totalPages}
            </div>
          </div>
        )}
    </div>
  );
};

export default DPBarMultiple;
